import React, { useState, useMemo, useEffect } from 'react'
import injectSheet from 'react-jss'
import Button from 'pobble-components/dist/button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar'
import posthog from 'posthog-js'
import { useChargebeeScript } from 'pobble-components/dist/subscriptions/utils'
import { useHistory, useLocation } from 'react-router-dom'
import { apiClient } from 'pobble-components/dist/api'
import illustration01 from './fsu-illustration-01.jpg'
import illustration02 from './fsu-illustration-02.jpg'
import illustration03 from './fsu-illustration-03.jpg'

const currencyMap = {
  AUD: 'A$',
  NZD: 'NZ$',
  USD: '$',
  GBP: '£'
}

const UpgradeButton = injectSheet({
  button: {
    margin: '12px auto 0',
    background: 'var(--yellow) !important',
    color: 'var(--gray) !important',
    border: '1px solid var(--gray) !important',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    '&:hover': {
      background: 'var(--yellow-) !important'
    }
  },
  outlineButton: {
    margin: '12px auto 0',
    background: 'transparent !important',
    color: '#00a1e9 !important',
    border: '1px solid #00a1e9 !important',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    '&:hover': {
      background: 'transparent !important'
    }
  }
})(({ id, classes, children, freeTrial, outline }) => {
  const { openCheckout } = useChargebeeScript()
  const history = useHistory()
  const location = useLocation()

  return (
    <Button
      minw
      darkBlue
      action={() => {
        history.replace({
          pathname: location.pathname,
          search: `from=${id}`
        })
        posthog.capture('user clicked fsu upsell cta', {
          id,
          add_trial: freeTrial
        })
        openCheckout({
          url: 'v3/me/subscriptions/checkout_new_pobble_silver_js',
          params: { annual: true, add_trial: freeTrial, discount: !freeTrial },
          options: {
            step: step => {
              posthog.capture('checkout step', {
                id,
                step
              })
            },
            close: () => {
              history.replace({
                pathname: location.pathname,
                search: false
              })
            }
          }
        })
      }}
      className={`${
        outline ? classes.outlineButton : classes.button
      } metrics__subscription_modal metrics__subscription_modal--${id}`}
      tall>
      {children}
    </Button>
  )
})

const FEATURES = [
  {
    title: 'Never start from a blank page again',
    text: '365 writing prompts',
    image: illustration01,
    alt: 'Calendar and pencil'
  },
  {
    title: 'Activities that work for every learner',
    text: 'Differentiated lessons',
    image: illustration02,
    alt: 'Differentiated lessons'
  },
  {
    title: 'Save hours of planning every week',
    text: 'Ready-made resources',
    image: illustration03,
    alt: 'Ready-made resources'
  }
]

export const FSUupsellWithDiscount = injectSheet({
  wrapper: {
    textAlign: 'center'
  },
  intro: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    marginBottom: '32px',
    textAlign: 'left',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
      alignItems: 'center',
      gap: '40px'
    }
  },
  introCopy: {
    flex: 1,
    '& h2': {
      margin: '0 0 12px',
      textAlign: 'left'
    }
  },
  introText: {
    font: 'var(--body)',
    color: 'var(--gray-)',
    margin: '0 0 16px'
  },
  testimonial: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    font: 'var(--body-)',
    color: 'var(--gray-)'
  },
  stars: {
    display: 'flex',
    gap: '0',
    color: 'var(--yellow)',
    fontSize: '10px'
  },
  quote: {
    margin: 0,
    fontStyle: 'italic',
    fontSize: '12px'
  },
  features: {
    flex: 1,
    padding: 0,
    margin: 0,
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    paddingTop: '30px'
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    textAlign: 'left'
  },
  featureCopy: {
    flex: 1,
    minWidth: 0
  },
  featureTitle: {
    font: 'var(--body)',
    fontWeight: 'bold',
    margin: '0 0 4px'
  },
  featureText: {
    font: 'var(--body-)',
    color: 'var(--gray-)',
    margin: 0
  },
  featureImage: {
    flexShrink: 0,
    width: '64px',
    height: '64px',
    objectFit: 'contain'
  },
  cardsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    paddingTop: '12px'
  },
  cardsRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    '@media (min-width: 768px)': {
      flexDirection: 'row'
    }
  },
  card: {
    flex: 1,
    position: 'relative',
    border: '1px solid var(--light-gray)',
    borderRadius: '12px',
    padding: '20px',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    textAlign: 'center',
    paddingTop: '30px'
  },
  cardPrimary: {
    border: '2px solid var(--yellow)'
  },
  recommended: {
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'var(--yellow)',
    color: 'var(--gray)',
    font: 'var(--body-)',
    fontWeight: 'bold',
    padding: '4px 12px',
    borderRadius: '999px',
    whiteSpace: 'nowrap'
  },
  cardHeader: {
    fontWeight: 'bold',
    fontSize: '16px'
  },
  cardList: {
    padding: 0,
    margin: 0,
    listStyle: 'none',
    font: 'var(--body)',
    '& li': {
      marginBottom: '6px'
    }
  },
  cardCTA: {
    marginTop: 'auto'
  },
  cardSmall: {
    fontSize: '12px',
    color: 'var(--gray-)'
  },
  cardInfo: {
    border: 'none',
    alignSelf: 'flex-end',
    flex: 'none',
    textAlign: 'center',
    paddingTop: '20px',
    width: '100%',
    '@media (min-width: 768px)': {
      width: 'calc(50% - 8px)'
    }
  },
  cardInfoCopy: {
    font: 'var(--body-)',
    color: '#555',
    margin: 0
  },
  freeAccountButton: {
    margin: '12px 0 0',
    background: 'transparent !important',
    color: '#555 !important',
    border: '1px solid #ccc !important',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    padding: '20px 10px',
    '&:hover': {
      background: 'transparent !important'
    }
  },
  thankYouTitle: {
    margin: '0 0 16px',
    textAlign: 'center'
  },
  thankYouIntro: {
    font: 'var(--body)',
    color: 'var(--gray-)',
    margin: '0 0 24px',
    textAlign: 'center',
    lineHeight: '24px'
  },
  thankYouFeaturesBox: {
    border: '1px solid #ccc',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px'
  },
  thankYouFeatures: {
    padding: 0,
    margin: 0,
    listStyle: 'none'
  },
  thankYouFeature: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    textAlign: 'left',
    '&:not(:last-child)': {
      marginBottom: '24px'
    }
  },
  thankYouFooter: {
    font: 'var(--body)',
    color: 'var(--gray-)',
    margin: '0 0 24px',
    textAlign: 'center',
    lineHeight: '24px'
  },
  exploreButton: {
    margin: '0 auto',
    background: 'var(--yellow) !important',
    color: 'var(--gray) !important',
    border: '1px solid var(--gray) !important',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    '&:hover': {
      background: 'var(--yellow-) !important'
    }
  }
})(({ classes, onGoToPobble }) => {
  const id = 'fsu'
  const [showThankYou, setShowThankYou] = useState(false)
  const [isCreatingTrial, setIsCreatingTrial] = useState(false)
  const [subsData, setSubsData] = useState(null)

  const price = useMemo(
    () => {
      if (!subsData) return null
      const annualAmount = parseFloat(subsData.pobble_silver_annual)

      return {
        currency: currencyMap[subsData.currency],
        annualPerMonth: (annualAmount / 12).toFixed(0),
        discountedAnnual: Math.round(annualAmount * 0.6)
      }
    },
    [subsData]
  )

  useEffect(() => {
    apiClient
      .get('v3/subscriptions/marketing_data')
      .then(res => setSubsData(res.data.subscription_marketing_data))
  }, [])

  const handleContinueWithFreeAccount = () => {
    posthog.capture('user clicked continue with free account on fsu upsell', {
      id,
      add_trial: true,
      annual: true
    })
    setIsCreatingTrial(true)
    apiClient
      .post('/v3/me/subscriptions/create_new_pobble_silver', null, {
        params: { annual: true, add_trial: true }
      })
      .then(() => setShowThankYou(true))
      .catch(() => onGoToPobble())
      .finally(() => setIsCreatingTrial(false))
  }

  if (showThankYou) {
    return (
      <div className={classes.wrapper}>
        <h2 className={classes.thankYouTitle}>As a little thank you...</h2>
        <p className={classes.thankYouIntro}>
          We've unlocked every premium feature for your first 14 days for free
          so you can properly experience Pobble before deciding whether to
          upgrade.
          <br />
          No payment details required.
        </p>

        <div className={classes.thankYouFeaturesBox}>
          <ul className={classes.thankYouFeatures}>
            {FEATURES.map(feature => (
              <li key={feature.title} className={classes.thankYouFeature}>
                <img
                  className={classes.featureImage}
                  src={feature.image}
                  alt={feature.alt}
                  width={64}
                  height={64}
                />
                <div className={classes.featureCopy}>
                  <p className={classes.featureTitle}>{feature.title}</p>
                  <p className={classes.featureText}>{feature.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className={classes.thankYouFooter}>
          We&apos;ll let you know when the trial ends.
          <br />
          After that, you&apos;ll keep your free account. Simple!
        </p>

        <Button
          minw
          darkBlue
          tall
          action={() => {
            posthog.capture(
              'user clicked start exploring pobble on fsu upsell',
              {
                id
              }
            )
            onGoToPobble()
          }}
          className={classes.exploreButton}>
          Start exploring Pobble
        </Button>
      </div>
    )
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.intro}>
        <div className={classes.introCopy}>
          <h2>A writing lesson for every day of the year</h2>
          <p className={classes.introText}>
            Say goodbye to lesson planning stress and blank-page panic. With
            Pobble Premium, you'll have everything you need to inspire every
            student to write confidently.
          </p>
          <div className={classes.testimonial}>
            <div className={classes.stars} aria-label="5 stars">
              {[...Array(5)].map((_, index) => (
                <FontAwesomeIcon key={index} icon={faStar} />
              ))}
            </div>
            <p className={classes.quote}>
              "I can't imagine teaching without it!" - Year 4 Teacher
            </p>
          </div>
        </div>

        <ul className={classes.features}>
          {FEATURES.map(feature => (
            <li key={feature.title} className={classes.feature}>
              <div className={classes.featureCopy}>
                <p className={classes.featureTitle}>{feature.title}</p>
                <p className={classes.featureText}>{feature.text}</p>
              </div>
              <img
                className={classes.featureImage}
                src={feature.image}
                alt={feature.alt}
                width={64}
                height={64}
              />
            </li>
          ))}
        </ul>
      </div>

      <div className={classes.cardsSection}>
        <div className={classes.cardsRow}>
          <div className={`${classes.card} ${classes.cardPrimary}`}>
            <div className={classes.recommended}>Recommended</div>
            <div className={classes.cardHeader}>
              Start planning better writing lessons today
            </div>

            <div>
              {price ? (
                <>
                  Just {price.currency}
                  {price.discountedAnnual}
                  /year, billed annually (Save 40%)
                </>
              ) : (
                'Billed annually'
              )}
            </div>

            <div className={classes.cardCTA}>
              <UpgradeButton id={`${id}-discount`} freeTrial={false}>
                Save 40% – buy now
              </UpgradeButton>
            </div>

            <div className={classes.cardSmall}>Best discount available</div>
          </div>

          <div className={`${classes.card}`}>
            <div className={classes.cardHeader}>
              Try Pobble with a 14-day trial
            </div>

            <div>Try everything free for 14 days</div>

            <div className={classes.cardCTA}>
              <UpgradeButton freeTrial={true} id={id} outline>
                Try free for 14 days
              </UpgradeButton>
            </div>

            <div className={classes.cardSmall}>
              Card required - cancel before day 14 and you won't be charged
            </div>
          </div>
        </div>

        <div className={`${classes.card} ${classes.cardInfo}`}>
          <p className={classes.cardInfoCopy}>
            After your 14 day trial, continue to enjoy Pobble Premium for just{' '}
            {price && (
              <>
                {price.currency}
                {price.annualPerMonth}
                /month
              </>
            )}
            , billed annually.
          </p>

          <div className={classes.cardCTA}>
            <Button
              className={classes.freeAccountButton}
              tertiary
              disabled={isCreatingTrial}
              onClick={handleContinueWithFreeAccount}>
              Or continue with a free account
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
})
